// searchWords — the Search page's words for its closed sums (kinds, sort
// fields, directions), localised at the render edge; and its icons.

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { IconName } from "../Icon/Icon.js";
import type { Recency } from "../Search/searchModel.js";
import type {
    MaterialKind,
    MaterialSortField,
    SearchTab,
    SortOrder,
    SourceSortField,
} from "./searchRows.js";

export const materialKindIcon = (kind: MaterialKind): IconName => {
    switch (kind) {
        case "lesson":
            return "sources";
        case "quiz":
            return "mcq";
        case "document":
            return "document";
        case "module":
            return "module";
        case "course":
            return "course";
        case "package":
            return "export";
    }
};

export const tabIcon = (tab: SearchTab): IconName => {
    switch (tab) {
        case "sources":
            return "source";
        case "materials":
            return "sources";
    }
};

// A sort field is a date (Newest first / Oldest first), or a name, kind or
// count (A to Z / Z to A); Best match has no direction.
type Direction = "date" | "text" | "none";

export const directionOf = (
    field: MaterialSortField | SourceSortField,
): Direction => {
    switch (field) {
        case "changed":
        case "added":
            return "date";
        case "name":
        case "kind":
        case "project":
        case "type":
        case "used-in":
            return "text";
        case "best":
            return "none";
    }
};

export const useSearchWords = () => {
    const { t } = useLingui();
    const kind = (k: MaterialKind): string => {
        switch (k) {
            case "lesson":
                return t`Lesson`;
            case "quiz":
                return t`Quiz`;
            case "document":
                return t`Document`;
            case "module":
                return t`Module`;
            case "course":
                return t`Course`;
            case "package":
                return t`Package`;
        }
    };
    const field = (f: MaterialSortField | SourceSortField): string => {
        switch (f) {
            case "best":
                return t`Best match`;
            case "changed":
                return t`Changed`;
            case "added":
                return t`Added`;
            case "name":
                return t`Name`;
            case "kind":
                return t`Kind`;
            case "project":
                return t`Project`;
            case "type":
                return t`Type`;
            case "used-in":
                return t`Used in`;
        }
    };
    // The direction, worded for the field.
    const order = (direction: Direction, o: SortOrder): string => {
        switch (direction) {
            case "date":
                return o === "descending" ? t`Newest first` : t`Oldest first`;
            case "text":
            case "none":
                return o === "ascending" ? t`A to Z` : t`Z to A`;
        }
    };
    // "No quizzes match “volcano”": names what was searched.
    const noMatch = (
        tab: SearchTab,
        k: MaterialKind | undefined,
        text: string,
    ): string => {
        if (tab === "sources") return t`No sources match “${text}”`;
        switch (k) {
            case "lesson":
                return t`No lessons match “${text}”`;
            case "quiz":
                return t`No quizzes match “${text}”`;
            case "document":
                return t`No documents match “${text}”`;
            case "module":
                return t`No modules match “${text}”`;
            case "course":
                return t`No courses match “${text}”`;
            case "package":
                return t`No packages match “${text}”`;
            case undefined:
                return t`No materials match “${text}”`;
        }
    };
    // When it last changed, as the material row says it.
    const changed = (recency: Recency, date: string): string => {
        switch (recency.type) {
            case "now":
            case "minutes":
            case "hours":
                return t`changed today`;
            case "yesterday":
                return t`changed yesterday`;
            case "days": {
                const days = recency.n;
                return t`${plural(days, { one: "changed # day ago", other: "changed # days ago" })}`;
            }
            case "date":
            case "raw":
            case "unknown":
                return date === "" ? "" : t`changed ${date}`;
        }
    };
    const usedIn = (n: number): string =>
        n === 0
            ? t`Not used yet`
            : t`${plural(n, { one: "used in # project", other: "used in # projects" })}`;
    return { kind, field, order, noMatch, changed, usedIn };
};
